/**
 * ============================================================================
 *  SINGLE SOURCE OF TRUTH FOR SITE-WIDE CONFIGURATION
 * ============================================================================
 *
 * Everything environment-specific lives here: the canonical domain, search-engine
 * verification tokens, analytics IDs, and the contact endpoint. The React app AND the
 * Node build scripts (sitemap, robots, prerender) all read this one file, so changing
 * the domain is a one-line edit rather than a hunt through the codebase.
 *
 * ---------------------------------------------------------------------------
 *  ⚠️  PUBLIC VALUES ONLY — NEVER PUT A SECRET IN THIS FILE
 * ---------------------------------------------------------------------------
 * Anything here is compiled into the JavaScript bundle and is readable by any visitor.
 * That is fine for a domain, a GA4 measurement ID or a Search Console token — those are
 * public by design. It is NOT fine for SMTP passwords or API keys.
 *
 * Secrets belong server-side:
 *   • SMTP / Gmail app password  →  frontend/public/api/config.php   (never committed)
 *   • FastAPI backend settings   →  backend/.env                     (never committed)
 *
 * ---------------------------------------------------------------------------
 *  OVERRIDING PER ENVIRONMENT
 * ---------------------------------------------------------------------------
 * Every value below can be overridden with an environment variable in `frontend/.env`
 * (copy `.env.example` to get started). The defaults are the production values, so a
 * plain `npm run build` with no .env produces the live site correctly.
 *
 * Note: Create React App only substitutes literal `process.env.REACT_APP_*` expressions
 * at build time, which is why each one is written out explicitly rather than looked up
 * through a helper.
 */

const pick = (value, fallback) => {
  const v = typeof value === "string" ? value.trim() : value;
  return v === undefined || v === null || v === "" ? fallback : v;
};

/** Strip any trailing slash so URL joins never produce a double slash. */
const trimEnd = (url) => String(url).replace(/\/+$/, "");

/** Normalise a base path to either "" (domain root) or "/segment". */
const normalisePath = (p) => {
  const clean = String(p || "").trim().replace(/^\/*/, "").replace(/\/+$/, "");
  return clean ? `/${clean}` : "";
};

// ---------------------------------------------------------------------------
// Where the site lives
// ---------------------------------------------------------------------------
export const SITE = {
  /**
   * Canonical origin, no trailing slash. This is what canonical tags, Open Graph URLs,
   * the sitemap and all structured data are built from. It must match how Google
   * actually reaches the site, including https and www-or-not.
   */
  url: trimEnd(pick(process.env.REACT_APP_SITE_URL, "https://snapxtechnologies.com")),

  /**
   * Sub-directory the build is served from, if any.
   *   ""       → https://snapxtechnologies.com/          (domain root)
   *   "/snapx" → https://snapxtechnologies.com/snapx/    (subfolder hosting)
   *
   * Leaving this wrong is an SEO problem, not a cosmetic one: canonical URLs would
   * point at addresses that return 404.
   */
  basePath: normalisePath(pick(process.env.REACT_APP_BASE_PATH, "")),

  name: "Snapx Technologies",
  legalName: "Snapx Technologies LLP",
  locale: "en-IN",
  language: "en",
  foundingDate: "2021-07-06",
  logoPath: "/assets/snapx-logo.png",
  /**
   * Social share card shown by WhatsApp, Facebook, LinkedIn, Slack and X.
   * Must stay 1200x630 — regenerate with `npm run og` after editing scripts/og/card.html.
   */
  ogImagePath: "/assets/og-image.png",
  ogImageWidth: 1200,
  ogImageHeight: 630,
  themeColor: "#050505",
};

/** Absolute canonical origin including the base path, e.g. https://example.com/snapx */
export const CANONICAL_ORIGIN = `${SITE.url}${SITE.basePath}`;

// ---------------------------------------------------------------------------
// Contact details (single source — used by the footer, contact page and schema)
// ---------------------------------------------------------------------------
export const CONTACT = {
  email: pick(process.env.REACT_APP_CONTACT_EMAIL, "hello@snapxtechnologies.com"),
  phone: pick(process.env.REACT_APP_CONTACT_PHONE, "+91 790 361 2979"),
  phoneE164: pick(process.env.REACT_APP_CONTACT_PHONE_E164, "+91-790-361-2979"),
  whatsapp: pick(process.env.REACT_APP_WHATSAPP_NUMBER, "917903612979"),
};

/**
 * WhatsApp chat link with a first line already typed. A blank chat asks the visitor to
 * compose an opening message, and many close it instead; a pre-filled one is a single tap.
 */
export const whatsappUrl = (text = "Hi Snapx, I found you on your website and would like to discuss a project.") =>
  `https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(text)}`;

// ---------------------------------------------------------------------------
// Lead capture endpoint
// ---------------------------------------------------------------------------
export const LEADS = {
  /**
   * Path the enquiry forms POST to, relative to the deployed base path.
   * The PHP mailer at `public/api/contact.php` handles SMTP delivery; its credentials
   * live in `public/api/config.php`, which is server-side and must never be committed.
   *
   * If the POST fails (no PHP runtime in local dev, network down), the form falls back
   * to storing the lead in localStorage so nothing is silently lost — see lib/api.js.
   */
  endpoint: pick(process.env.REACT_APP_LEAD_ENDPOINT, "/api/contact.php"),

  /** Optional absolute origin, if the mailer runs on a different host to the site. */
  origin: trimEnd(pick(process.env.REACT_APP_BACKEND_URL, "")),

  /**
   * Development only. When the POST fails, store the lead in the visitor's own
   * localStorage and report success — handy where there is no PHP runtime.
   *
   * It must stay off in production. A lead in the visitor's browser never reaches us,
   * yet the visitor is told it was sent, so they wait for a reply that never comes.
   * (A mail outage does not need it: contact.php saves the lead to its file before
   * attempting SMTP, and answers success either way.) With it off, a failed send shows
   * the visitor an error with our email and WhatsApp, so they can reach us directly.
   */
  localFallback:
    pick(process.env.REACT_APP_LEAD_LOCAL_FALLBACK, process.env.NODE_ENV === "development" ? "true" : "false") !== "false",
};

// ---------------------------------------------------------------------------
// Search engine verification
// ---------------------------------------------------------------------------
/**
 * Paste the token only — not the whole meta tag.
 *
 * Google Search Console → Add property → URL prefix → "HTML tag" method gives you:
 *   <meta name="google-site-verification" content="AbC123..." />
 * You want just the `AbC123...` part here (or in REACT_APP_GOOGLE_SITE_VERIFICATION).
 *
 * Leave a value empty and no tag is rendered at all — an empty verification tag is
 * worse than none.
 */
export const VERIFICATION = {
  google: pick(process.env.REACT_APP_GOOGLE_SITE_VERIFICATION, ""),
  bing: pick(process.env.REACT_APP_BING_SITE_VERIFICATION, ""),
  pinterest: pick(process.env.REACT_APP_PINTEREST_VERIFICATION, ""),
  facebookDomain: pick(process.env.REACT_APP_FACEBOOK_DOMAIN_VERIFICATION, ""),
};

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------
/**
 * All optional. Empty = the script is never loaded, so an unconfigured site ships
 * zero third-party requests. Everything loads `async` so it cannot block first paint.
 */
export const ANALYTICS = {
  /** GA4 measurement ID, e.g. "G-XXXXXXXXXX" */
  ga4: pick(process.env.REACT_APP_GA4_ID, ""),
  /** Google Tag Manager container, e.g. "GTM-XXXXXXX" */
  gtm: pick(process.env.REACT_APP_GTM_ID, ""),
  /** Plausible: set the domain to enable, e.g. "snapxtechnologies.com" */
  plausibleDomain: pick(process.env.REACT_APP_PLAUSIBLE_DOMAIN, ""),
  /** Honour Do Not Track before loading anything. */
  respectDoNotTrack: pick(process.env.REACT_APP_RESPECT_DNT, "true") !== "false",
};

// ---------------------------------------------------------------------------
// SEO defaults
// ---------------------------------------------------------------------------
export const SEO_DEFAULTS = {
  titleSuffix: "Snapx Technologies",
  defaultTitle: "Snapx Technologies — We build products, brands, businesses",
  defaultDescription:
    "Snapx Technologies is a product + services company from Patna, India. Creators of EduAnant, India's school operating system — plus web, mobile, branding, motion, marketing and finance services.",
  /** Set false on a staging deploy to keep it out of the index entirely. */
  indexable: pick(process.env.REACT_APP_INDEXABLE, "true") !== "false",
};

export default { SITE, CONTACT, LEADS, VERIFICATION, ANALYTICS, SEO_DEFAULTS, CANONICAL_ORIGIN };
